import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthUser } from "../types/authTypes";

/** Company details from social-login response; used to pre-fill onboarding form */
export interface CompanyDetailsFromAuth {
  company_name?: string | null;
  company_website?: string | null;
  industry_type?: string | null;
  location?: string | null;
  address?: string | null;
  /** Top-level `owner_name` from social-login `data` (merged in on dispatch). */
  owner_name?: string | null;
}

/** Merge `company_details` with top-level `owner_name` from `/accounts/social-login/` response `data`. */
export function companyDetailsPayloadFromLoginData(
  data:
    | {
        company_details?: Partial<CompanyDetailsFromAuth> | null;
        owner_name?: string | null;
      }
    | null
    | undefined,
): CompanyDetailsFromAuth | null {
  if (!data) return null;
  const cd = data.company_details;
  const ownerTrimmed = String(data.owner_name ?? "").trim();
  if (!cd && !ownerTrimmed) return null;
  const base = { ...(cd ?? {}) } as CompanyDetailsFromAuth;
  if (ownerTrimmed) base.owner_name = ownerTrimmed;
  return base;
}

export interface AuthState {
  user: AuthUser | null;
  token: string | null;
  jobseeker_id: number | null;
  /** New-user flag from API (snake_case). Consumers must use state.auth.is_new_user, not isNewUser. */
  is_new_user: boolean | null;
  role: number | null;
  redirectUrl: string | null;
  /** Company details from social-login; pre-fill company onboarding form */
  company_details: CompanyDetailsFromAuth | null;
  subscription_status: string | null;
  loading: boolean;
  error: string | null;
  temp_resume: any;
  isResumeEnhanced: boolean;
  /** From GET /accounts/user-credits/; refreshed on app load for non–jobseeker roles. */
  userCredits: number | null;
  /** True while GET /accounts/user-credits/ is in flight (UI can show placeholder vs 0). */
  userCreditsLoading: boolean;
}

// export interface LoginState {
//   login: ConfirmationResult;
//   loading: boolean;
//   error: string | null;
// }
const initialState: AuthState = {
  user: null,
  token: null,
  jobseeker_id: null,
  is_new_user: null,
  role: null,
  redirectUrl: null,
  company_details: null,
  subscription_status: null,
  loading: false,
  error: null,
  temp_resume: null,
  isResumeEnhanced: false,
  userCredits: null,
  userCreditsLoading: false,
  // login: {
  //   verificationId: "",
  //   confirm: function (verificationCode: string): Promise<UserCredential> {
  //     throw new Error("Function not implemented.");
  //   },
  // },
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state: AuthState, action: PayloadAction<AuthUser>) => {
      state.user = action.payload;
      state.loading = false;
    },
    setTempResume: (state: AuthState, action: PayloadAction<AuthUser>) => {
      state.temp_resume = action.payload;
      state.loading = false;
    },
    setAuthToken: (state: AuthState, action: PayloadAction<string>) => {
      state.token = action.payload;
      // Token is now stored in sessionStorage via redux-persist
      // No need to set cookie - allows tab-isolated sessions
    },
    setRole: (state: AuthState, action: PayloadAction<number>) => {
      state.role = action.payload;
    },
    setRedirectUrl: (
      state: AuthState,
      action: PayloadAction<string | null>,
    ) => {
      state.redirectUrl = action.payload;
    },
    setCompanyDetails: (
      state: AuthState,
      action: PayloadAction<CompanyDetailsFromAuth | null>,
    ) => {
      state.company_details = action.payload;
    },
    setUserName: (state: AuthState, action: PayloadAction<string>) => {
      if (state.user) {
        state.user.full_name = action.payload;
      }
    },
    setSubscriptionStatus: (
      state: AuthState,
      action: PayloadAction<string | null>,
    ) => {
      state.subscription_status = action.payload;
    },
    setJobseekerId: (state: AuthState, action: PayloadAction<number>) => {
      state.jobseeker_id = action.payload;
    },
    setNewUser: (state: AuthState, action: PayloadAction<boolean>) => {
      state.is_new_user = action.payload;
    },
    setUserId: (state: AuthState, action: PayloadAction<string>) => {
      if (state.user) {
        state.user.user_id = action.payload;
      }
    },
    updateUserCompanyId: (
      state: AuthState,
      action: PayloadAction<number | null>,
    ) => {
      if (state.user) {
        state.user.company_id = action.payload;
      }
    },
    // setLoginResult: (
    //   state: LoginState,
    //   action: PayloadAction<ConfirmationResult>
    // ) => {
    //   state.login = action.payload;
    // },
    updateUserPhoneNumber: (
      state: AuthState,
      action: PayloadAction<{ countryCode: number; phone: string }>,
    ) => {
      if (state.user) {
        state.user.country_code = action.payload.countryCode;
        state.user.phone = action.payload.phone;
      }
    },

    resetUser: () => initialState,
    setUserRole: (state: AuthState, action: PayloadAction<number>) => {
      if (state.user) {
        state.user.role = action.payload;
      } else {
      }
    },
    setIsResumeEnahnced: (state: AuthState, action: PayloadAction<boolean>) => {
      state.isResumeEnhanced = action.payload;
    },
    setUserCredits: (
      state: AuthState,
      action: PayloadAction<number | null>,
    ) => {
      state.userCredits = action.payload;
    },
    setUserCreditsLoading: (
      state: AuthState,
      action: PayloadAction<boolean>,
    ) => {
      state.userCreditsLoading = action.payload;
    },

    setLoading: (state: AuthState, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state: AuthState, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setUser,
  setLoading,
  setError,
  setRole,
  setUserName,
  setUserRole,
  setAuthToken,
  resetUser,
  setUserId,
  updateUserCompanyId,
  setRedirectUrl,
  setCompanyDetails,
  setJobseekerId,
  setNewUser,
  // setLoginResult,
  updateUserPhoneNumber,
  setTempResume,
  setIsResumeEnahnced,
  setUserCredits,
  setUserCreditsLoading,
  setSubscriptionStatus,
} = authSlice.actions;
export default authSlice.reducer;
